import { array } from 'fp-ts';
import { filter, flatten, intersection, map } from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/function';
import { keys } from 'fp-ts/Record';
import { Eq as stringEq } from 'fp-ts/string';
import { get, isEmpty, isNil, negate, some, uniq, values } from 'lodash/fp';
import { createSelector, Selector } from 'reselect';
import { getOrFalse, getStringProp } from '@mews/fp';
import { parentSelector } from '../../selectors/parentSelector';
import { ApplicationState } from '../../types';
import { getCurrencyCode } from '../app/selectors';
import { getRateIds } from '../entities/rates/selectors';
import { EndpointOccupancy, Occupancy, OccupancyData } from '../resourceOrder/occupancyTypes';
import {
    areOccupanciesNonEmpty,
    getEndDate,
    getEndTimeUtc,
    getOccupancies,
    getProductIds,
    getRoomId,
    getSelectedHotelId,
    getStartDate,
    getStartTimeUtc,
    getUniqueOccupancies,
    getVoucherCode,
} from '../resourceOrder/selectors';
import { getPricingHash } from './getPricingHash';
import { hasPricingCurrencyPrices, haveAllPricingRatesCurrencyCode } from './pricingHelpers';
import { OccupancyPricings, Pricing, PricingValue } from './types';

export const getPricing = createSelector<ApplicationState, ApplicationState, Pricing>(
    parentSelector,
    get(['data', 'pricing'])
);

const createSelectedOccupancyPricingSelector = (occupancySelector: Selector<ApplicationState, Occupancy[]>) =>
    createSelector(
        getPricing,
        getStartDate,
        getEndDate,
        getRoomId,
        getSelectedHotelId,
        occupancySelector,
        getVoucherCode,
        getProductIds,
        getStartTimeUtc,
        getEndTimeUtc,
        (
            pricing,
            startDate,
            endDate,
            categoryId,
            enterpriseId,
            occupancies,
            voucherCode,
            productIds,
            startTimeUtc,
            endTimeUtc
        ) =>
            pipe(
                occupancies,
                map((occupancy) => {
                    const occupancyData = values(occupancy.occupancyData);

                    return {
                        occupancy: { occupancyData },
                        pricing:
                            pricing[
                                getPricingHash({
                                    startDate: startTimeUtc ?? startDate,
                                    endDate: endTimeUtc ?? endDate,
                                    enterpriseId,
                                    categoryId,
                                    voucherCode,
                                    productIds,
                                    occupancyData,
                                })
                            ],
                    };
                })
            )
    );

export const getSelectedOccupancyPricings = createSelectedOccupancyPricingSelector(getOccupancies);

const getSelectedUniqueOccupancyPricings = createSelectedOccupancyPricingSelector(getUniqueOccupancies);

const filterExistingCurrencyCodePricing = (currencyCode: string) =>
    filter(
        flow(
            get('pricing'),
            (pricing?: PricingValue) =>
                isNil(pricing) || isNil(pricing?.rates) || !haveAllPricingRatesCurrencyCode(currencyCode, pricing)
        )
    );

const mapOccupancyAndFilterOutEmpty = (occupancyPricings: OccupancyPricings[]): EndpointOccupancy[] =>
    pipe(
        occupancyPricings,
        array.map((pricing) => pricing.occupancy),
        array.filter((occupancy) =>
            // Return true for occupancies with at least one person
            pipe(
                occupancy.occupancyData,
                array.some((occupancyData: OccupancyData) => occupancyData.personCount > 0)
            )
        )
    );

export const getSelectedCategoryUnavailableOccupancyPricing = createSelector(
    getSelectedUniqueOccupancyPricings,
    getCurrencyCode,
    (occupancyPricings, currencyCode) => {
        if (currencyCode == null) {
            return mapOccupancyAndFilterOutEmpty(occupancyPricings);
        }

        return flow(filterExistingCurrencyCodePricing(currencyCode), mapOccupancyAndFilterOutEmpty)(occupancyPricings);
    }
);

export const getSelectedUniqueOccupancyPricingsRateIds = createSelector(
    getSelectedUniqueOccupancyPricings,
    flow(
        filter((uniqueOccupancyPricing) => uniqueOccupancyPricing.pricing != null),
        map((uniqueOccupancyPricing) => uniqueOccupancyPricing.pricing.rates),
        map(keys),
        flatten,
        uniq
    )
);

export const getSelectedCategoryAvailableRateIds = createSelector(
    getSelectedUniqueOccupancyPricingsRateIds,
    getRateIds,
    intersection(stringEq)
);

export const areAnySelectedOccupancyPricingFetching = createSelector(
    getSelectedUniqueOccupancyPricings,
    some(get(['pricing', 'isFetching']))
);

export const hasAnySelectedOccupancyPricingFailed = createSelector(
    getSelectedUniqueOccupancyPricings,
    some(get(['pricing', 'hasFailed']))
);

export const isAnyPricingFetching = createSelector(
    getPricing,
    flow(
        values,
        array.some((pricing) => pricing.isFetching ?? false)
    )
);

export const hasAnyPricingFailed = createSelector(getPricing, flow(values, some(getOrFalse('hasFailed'))));

const shouldGetSelectedOccupancyPricing = createSelector(
    getSelectedCategoryUnavailableOccupancyPricing,
    negate(isEmpty)
);

export const shouldGetSelectedNonEmptyOccupancyPricing = createSelector(
    shouldGetSelectedOccupancyPricing,
    areOccupanciesNonEmpty,
    (shouldGetPricing, occupanciesNonEmpty) => shouldGetPricing && occupanciesNonEmpty
);

export const getPricingValueByHash = createSelector(
    getStringProp('pricingHash'),
    getPricing,
    (pricingHash, pricingData) => pricingData[pricingHash]
);

export const hasPricingCurrencyCodeByHash = createSelector(
    getStringProp('rateId'),
    getStringProp('currencyCode'),
    getPricingValueByHash,
    hasPricingCurrencyPrices
);

export const hasPricingRatesCurrencyCode = createSelector(
    getStringProp('currencyCode'),
    getPricingValueByHash,
    haveAllPricingRatesCurrencyCode
);
